import { STORAGE_KEYS } from "../constants";

export type MusicBed = "menu" | "run" | "none";
export type SfxName =
  | "ui-move"
  | "ui-confirm"
  | "whoosh"
  | "jump"
  | "land"
  | "duck"
  | "pickup"
  | "growl"
  | "hit"
  | "life"
  | "fanfare"
  | "gameover"
  | "footstep";

interface AudioPrefs {
  music: boolean;
  sfx: boolean;
}

function loadPrefs(): AudioPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.audio);
    if (!raw) {
      return { music: true, sfx: true };
    }
    const parsed = JSON.parse(raw) as Partial<AudioPrefs>;
    return {
      music: parsed.music !== false,
      sfx: parsed.sfx !== false,
    };
  } catch {
    return { music: true, sfx: true };
  }
}

export class AudioManager {
  musicEnabled: boolean;
  sfxEnabled: boolean;
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private musicNodes: OscillatorNode[] = [];
  private musicInterval: number | null = null;
  private bed: MusicBed = "none";
  private footstepUntil = 0;
  private outputPaused = false;

  constructor() {
    const prefs = loadPrefs();
    this.musicEnabled = prefs.music;
    this.sfxEnabled = prefs.sfx;
  }

  get unlocked(): boolean {
    return this.ctx !== null && this.ctx.state === "running";
  }

  async unlock(): Promise<void> {
    if (!this.ctx) {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctx();
      this.master = this.ctx.createGain();
      this.musicGain = this.ctx.createGain();
      this.sfxGain = this.ctx.createGain();
      this.musicGain.connect(this.master);
      this.sfxGain.connect(this.master);
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === "suspended") {
      await this.ctx.resume();
    }
    this.applyGains();
  }

  setMusicEnabled(enabled: boolean): void {
    this.musicEnabled = enabled;
    this.persist();
    this.applyGains();
    if (enabled && this.bed !== "none" && this.ctx) {
      this.playMusic(this.bed);
    } else if (!enabled) {
      this.stopMusicOscillators();
    }
  }

  setSfxEnabled(enabled: boolean): void {
    this.sfxEnabled = enabled;
    this.persist();
    this.applyGains();
  }

  async toggleMusic(): Promise<void> {
    await this.unlock();
    this.setMusicEnabled(!this.musicEnabled);
  }

  async toggleSfx(): Promise<void> {
    await this.unlock();
    const enabling = !this.sfxEnabled;
    this.setSfxEnabled(enabling);
    if (enabling) {
      this.playSfx("ui-confirm");
    }
  }

  pauseOutput(): void {
    this.outputPaused = true;
    this.stopMusicOscillators();
    if (this.musicGain) {
      this.musicGain.gain.value = 0;
    }
  }

  resumeOutput(): void {
    this.outputPaused = false;
    this.applyGains();
    if (this.bed !== "none") {
      this.playMusic(this.bed);
    }
  }

  playMusic(bed: MusicBed): void {
    const alreadyPlaying =
      bed === this.bed &&
      bed !== "none" &&
      this.musicInterval !== null &&
      !this.outputPaused &&
      this.musicEnabled;
    if (alreadyPlaying) {
      return;
    }
    this.bed = bed;
    if (this.outputPaused || !this.ctx || !this.musicEnabled || bed === "none") {
      this.stopMusicOscillators();
      return;
    }
    this.stopMusicOscillators();
    const tempo = bed === "menu" ? 78 : 108;
    const notes = bed === "menu" ? [196, 247, 294, 330, 392] : [220, 262, 330, 392, 494];
    this.scheduleBed(notes, tempo, bed === "menu" ? 0.045 : 0.055);
  }

  playSfx(name: SfxName): void {
    if (!this.ctx || !this.sfxEnabled || !this.sfxGain || this.outputPaused) {
      return;
    }
    const now = this.ctx.currentTime;
    switch (name) {
      case "ui-move":
        this.tone(520, 0.06, "triangle", 0.04, now);
        break;
      case "ui-confirm":
        this.tone(440, 0.08, "triangle", 0.05, now);
        this.tone(660, 0.1, "triangle", 0.04, now + 0.06);
        break;
      case "whoosh":
        this.noise(0.12, 0.05, 900, now);
        break;
      case "jump":
        this.sweep(240, 520, 0.16, 0.06);
        break;
      case "land":
        this.noise(0.1, 0.06, 400, now);
        this.tone(140, 0.08, "sine", 0.05, now);
        break;
      case "duck":
        this.sweep(420, 180, 0.12, 0.05);
        break;
      case "pickup":
        this.tone(660, 0.07, "sine", 0.05, now);
        this.tone(880, 0.1, "sine", 0.04, now + 0.05);
        break;
      case "growl":
        this.tone(90, 0.22, "sawtooth", 0.05, now);
        this.tone(70, 0.18, "square", 0.03, now + 0.04);
        break;
      case "hit":
        this.noise(0.16, 0.07, 500, now);
        this.tone(160, 0.12, "square", 0.05, now);
        break;
      case "life":
        this.tone(220, 0.16, "triangle", 0.05, now);
        this.tone(165, 0.2, "sine", 0.04, now + 0.08);
        break;
      case "fanfare":
        this.tone(392, 0.18, "triangle", 0.06, now);
        this.tone(494, 0.18, "triangle", 0.06, now + 0.14);
        this.tone(587, 0.28, "triangle", 0.07, now + 0.28);
        break;
      case "gameover":
        this.tone(330, 0.2, "sine", 0.05, now);
        this.tone(247, 0.24, "sine", 0.05, now + 0.18);
        this.tone(196, 0.36, "sine", 0.06, now + 0.38);
        break;
      case "footstep":
        if (now < this.footstepUntil) {
          return;
        }
        this.footstepUntil = now + 0.28;
        this.noise(0.05, 0.03, 600, now);
        break;
    }
  }

  private applyGains(): void {
    if (this.musicGain) {
      this.musicGain.gain.value = this.musicEnabled && !this.outputPaused ? 1 : 0;
    }
    if (this.sfxGain) {
      this.sfxGain.gain.value = this.sfxEnabled && !this.outputPaused ? 1 : 0;
    }
  }

  private persist(): void {
    localStorage.setItem(
      STORAGE_KEYS.audio,
      JSON.stringify({ music: this.musicEnabled, sfx: this.sfxEnabled }),
    );
  }

  private stopMusicOscillators(): void {
    for (const node of this.musicNodes) {
      try {
        node.stop();
        node.disconnect();
      } catch {
        // already stopped
      }
    }
    this.musicNodes = [];
    if (this.musicInterval !== null) {
      window.clearInterval(this.musicInterval);
      this.musicInterval = null;
    }
  }

  private scheduleBed(notes: number[], tempo: number, gain: number): void {
    if (!this.ctx) {
      return;
    }
    const beat = 60 / tempo;
    let step = 0;
    const tick = (): void => {
      if (!this.ctx || !this.musicEnabled) {
        return;
      }
      const freq = notes[step % notes.length] ?? 220;
      this.musicTone(freq, beat * 1.4, gain);
      if (step % 4 === 0) {
        this.musicTone((notes[0] ?? 196) / 2, beat * 2.2, gain * 0.7);
      }
      step += 1;
    };
    tick();
    this.musicInterval = window.setInterval(tick, beat * 1000);
  }

  private musicTone(freq: number, duration: number, gainValue: number): void {
    if (!this.ctx || !this.musicGain) {
      return;
    }
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(gainValue, this.ctx.currentTime + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + duration);
    osc.connect(gain);
    gain.connect(this.musicGain);
    osc.start();
    osc.stop(this.ctx.currentTime + duration + 0.02);
    this.musicNodes.push(osc);
  }

  private tone(
    freq: number,
    duration: number,
    type: OscillatorType,
    gainValue: number,
    when: number,
  ): void {
    if (!this.ctx || !this.sfxGain) {
      return;
    }
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, when);
    gain.gain.exponentialRampToValueAtTime(gainValue, when + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, when + duration);
    osc.connect(gain);
    gain.connect(this.sfxGain);
    osc.start(when);
    osc.stop(when + duration + 0.02);
  }

  private sweep(from: number, to: number, duration: number, gainValue: number): void {
    if (!this.ctx || !this.sfxGain) {
      return;
    }
    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(from, now);
    osc.frequency.exponentialRampToValueAtTime(to, now + duration);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(gainValue, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    osc.connect(gain);
    gain.connect(this.sfxGain);
    osc.start(now);
    osc.stop(now + duration + 0.02);
  }

  private noise(duration: number, gainValue: number, cutoff: number, when: number): void {
    if (!this.ctx || !this.sfxGain) {
      return;
    }
    const length = Math.floor(this.ctx.sampleRate * duration);
    const buffer = this.ctx.createBuffer(1, length, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) {
      data[i] = Math.random() * 2 - 1;
    }
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    const filter = this.ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = cutoff;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(gainValue, when);
    gain.gain.exponentialRampToValueAtTime(0.0001, when + duration);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.sfxGain);
    source.start(when);
    source.stop(when + duration + 0.02);
  }
}

export const audioManager = new AudioManager();
